import {
  SUPPORTED_LOCALE_OPTIONS,
  type SupportedLocale,
  type SupportedLocaleOption,
} from './supportedLocales'
import { isSupportedLocale } from './util/localeMatcher'

interface LanguagesRemoteConfigLanguage {
  locale: string
  nativeName?: string
}

interface LanguagesRemoteConfig {
  visibleLanguages?: Array<LanguagesRemoteConfigLanguage>
  preReleaseLanguages?: Array<LanguagesRemoteConfigLanguage>
}

const supportedLocaleOptionsByLocale = new Map<SupportedLocale, SupportedLocaleOption>(
  SUPPORTED_LOCALE_OPTIONS.map(option => [option.locale, option]),
)

function isLanguagesRemoteConfigValue(
  value: unknown,
): value is LanguagesRemoteConfig {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function isLanguagesRemoteConfigLanguage(
  value: unknown,
): value is LanguagesRemoteConfigLanguage {
  return !!value && typeof value === 'object' && !Array.isArray(value) && 'locale' in value && typeof value.locale === 'string'
}

export function getVisibleLanguageOptions(
  remoteConfig: App.LabRemoteConfigResult | undefined,
) {
  const fallbackLanguageOptions = SUPPORTED_LOCALE_OPTIONS

  if (remoteConfig?.error) {
    return fallbackLanguageOptions
  }

  const { value } = remoteConfig ?? {}
  if (!isLanguagesRemoteConfigValue(value)) {
    return fallbackLanguageOptions
  }

  if (!Array.isArray(value.visibleLanguages)) {
    return fallbackLanguageOptions
  }

  const addedLocales = new Set<SupportedLocale>()
  const languageOptions = value.visibleLanguages.reduce<Array<SupportedLocaleOption>>((result, languageConfig) => {
    if (!isLanguagesRemoteConfigLanguage(languageConfig)) {
      return result
    }

    const { nativeName } = languageConfig
    const locale = languageConfig.locale.trim()
    if (!isSupportedLocale(locale)) {
      return result
    }

    if (addedLocales.has(locale)) {
      return result
    }

    const languageOption = supportedLocaleOptionsByLocale.get(locale)
    if (!languageOption) {
      return result
    }

    addedLocales.add(locale)
    const configuredNativeName = typeof nativeName === 'string' ? nativeName.trim() : undefined

    result.push({
      ...languageOption,
      nativeName: configuredNativeName || languageOption.nativeName,
    })
    return result
  }, [])

  if (languageOptions.length === 0) {
    return fallbackLanguageOptions
  }

  return languageOptions
}

export function getVisibleLanguageLocales(
  remoteConfig: App.LabRemoteConfigResult | undefined,
): Array<SupportedLocale> {
  return getVisibleLanguageOptions(remoteConfig).map(({ locale }) => locale)
}
